<template>
  <FencingBanner
    v-if="isFenced"
    data-testid="agency-widgets-fencing-banner"
    :headline="t('Reviews.shared.media.widgets.fencing.headline')"
    :feature-list="[
      t('Reviews.shared.media.widgets.fencing.item1'),
      t('Reviews.shared.media.widgets.fencing.item2'),
      t('Reviews.shared.media.widgets.fencing.item3'),
      t('Reviews.shared.media.widgets.fencing.item4'),
    ]"
    :cta-label="t('Reviews.shared.media.widgets.fencing.ctaLabel')"
    :cta-link="t('Reviews.shared.media.widgets.fencing.ctaLink')"
    :banner-image="widgetsFencingImageUrl"
    placement="banner"
    entity-type="service"
    :entity-id="agency.id"
  />
  <div data-testid="agency-widgets-section" class="flex flex-col py-6" :class="{ '!text-disabled': isFenced }">
    <div class="pb-8">
      <div class="flex items-center gap-2 pb-2 text-body-m-bold">
        {{ t('Reviews.shared.media.widgets.title.top') }}
        <TooltipWrapper v-if="!isFenced">
          <template #trigger>
            <a v-if="helpLink" :href="helpLink" rel="noopener noreferrer" target="_blank"
              ><CircleHelpIcon class="lucide-icon-s text-icon-subtlest"
            /></a>
            <CircleHelpIcon v-else class="lucide-icon-s text-icon-subtlest" />
          </template>
          <template #content>
            <div v-html="helpTooltip"></div>
          </template>
        </TooltipWrapper>
      </div>
      <div class="widget-description-top text-body-s" v-html="t('Reviews.agency.view.media.widgetDescription.top')"></div>
    </div>
    <div class="pb-4">
      <ChipBadge v-if="isFenced" rounded type="default" color="default"
        ><span class="text-body-s-bold text-disabled">Preview</span></ChipBadge
      >
      <ChipBadge v-else rounded type="default" color="warning"><span class="text-body-s-bold">Preview</span></ChipBadge>
    </div>
    <ResourcesList
      data-testid="agency-widgets-list"
      :resources="generatedWidget"
      image-preview-size="m"
      :is-fenced="isFenced"
      show-download
      entity-type="service"
      :entity-id="agency.id"
      resource-type="widget"
    />
    <div class="py-16">
      <div class="pb-2 text-body-m-bold">{{ t('Reviews.shared.media.widgets.title.bottom') }}</div>
      <div v-html="t('Reviews.shared.media.widgets.description.bottom')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChipBadge, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CircleHelpIcon } from '@ramp106/omrjs-core-ui/icons'
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { baseOmrUrl } from '@/config/env'
import type { AgencyData } from '@/gql/reviews'
import buildResourceWidgets from '@/helpers/buildResourceWidgets'

import FencingBanner from '../../ReviewsCommon/Fencing/FencingBanner.vue'
import widgetsFencingImageUrl from '../../ReviewsCommon/Fencing/widgets_fencing.svg'
import ResourcesList from '../../ReviewsCommon/Resources/ResourcesList.vue'
import { type Resource } from '../../ReviewsCommon/Resources/types'

const props = defineProps<{ agency: AgencyData }>()
const { t, locale } = useI18n()

const isFenced = computed(() => !props.agency.configFlags.widgetsManagementAllowed)
const helpTooltip = computed(() => t('Reviews.shared.media.widgets.help'))
const helpLink = computed(() => t('Reviews.shared.media.widgets.helpLink'))

const agencyWidgetPath = (template: string) =>
  reviewsPaths.agencyRatingWidget({ locale: locale.value, slug: props.agency.slug }, { template })

const reviewsPDPUrlWidget = computed<string>(
  () => baseOmrUrl + reviewsPaths.agency({ locale: locale.value, slug: props.agency.slug }, { utm_source: 'widget', utm_medium: 'vendor' }),
)

const generatedWidget: Ref<Resource[]> = computed(() =>
  buildResourceWidgets(
    [
      {
        id: '0',
        title: t('Reviews.shared.media.widgets.lightTitle'),
        link: agencyWidgetPath('light'),
      },
      {
        id: '1',
        title: t('Reviews.shared.media.widgets.darkTitle'),
        link: agencyWidgetPath('dark'),
      },
      {
        id: '2',
        title: t('Reviews.shared.media.widgets.transparentLightTitle'),
        link: agencyWidgetPath('transparentLight'),
      },
      {
        id: '3',
        title: t('Reviews.shared.media.widgets.transparentDarkTitle'),
        link: agencyWidgetPath('transparentDark'),
      },
    ],
    baseOmrUrl,
    props.agency.title,
    reviewsPDPUrlWidget.value,
  ),
)
</script>
<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.widget-description-top :deep(a) {
  @apply !font-bold !underline;
}
</style>
